import React from 'react';
import { Box, Typography, Grid, Card, CardContent } from '@mui/material';
import { saasModernTheme } from 'common/theme/saasModern';
import { useTranslations } from 'hooks/useTranslations';
import { gradientBoxStyleWithHoverAnimation } from 'utils/gradianStyle';
import CheckIcon from '@mui/icons-material/Check';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';

const PricingInfoCards = () => {
  const { t } = useTranslations();

  // Fallback data in case translations fail
  const fallbackData = {
    flexibility: {
      title: "Scale at Your Pace",
      credits: {
        title: "Flexible Credits",
        items: [
          "Increase your credit limit according to your needs",
          "From 5M to 1B credits available",
          "No waste, pay only for what you use"
        ]
      },
      teams: {
        title: "Scalable Team Plans",
        items: [
          "Add additional users ($15/month/user)",
          "Bonus credits included for each new user",
          "Centralized team and budget management"
        ]
      }
    },
    transparency: {
      title: "Total Transparency",
      usage: {
        title: "Usage-Based Pricing",
        items: [
          "Credit cost varies according to selected AI",
          "GPT-4, Claude, Gemini: different, transparent costs",
          "No hidden fees, real-time pricing"
        ]
      },
      dashboard: {
        title: "Monitoring Dashboard",
        items: [
          "Real-time tracking of your consumption",
          "Analysis by user, agent and AI provider"
        ]
      }
    },
    features: {
      title: "Full Access from the First Plan",
      core: {
        title: "Core Features (All Paid Plans)",
        items: [
          "Unlimited AI agents without restrictions",
          "Complete and intuitive chat interface",
          "Data import from 86 connector categories",
          "Access to all major AI providers"
        ]
      },
      enterprise: {
        title: "Enterprise Features (Team Plans Only)",
        items: [
          "Advanced permission and role management",
          "SSO/SAML for enterprise authentication",
          "Extended analytics and custom reports",
          "Priority support and white label"
        ]
      }
    }
  };

  const getItems = (path, fallback) => {
    const items = t(path, { returnObjects: true });
    return Array.isArray(items) ? items : fallback;
  };

  const cardData = [
    {
      id: 'flexibility',
      title: t('pricing.info.flexibility.title') || fallbackData.flexibility.title,
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: saasModernTheme.colors.primary }} />,
      sections: [
        {
          title: t('pricing.info.flexibility.credits.title') || fallbackData.flexibility.credits.title,
          items: getItems('pricing.info.flexibility.credits.items', fallbackData.flexibility.credits.items)
        },
        {
          title: t('pricing.info.flexibility.teams.title') || fallbackData.flexibility.teams.title,
          items: getItems('pricing.info.flexibility.teams.items', fallbackData.flexibility.teams.items)
        }
      ]
    },
    {
      id: 'transparency',
      title: t('pricing.info.transparency.title') || fallbackData.transparency.title,
      icon: <VisibilityIcon sx={{ fontSize: 40, color: saasModernTheme.colors.primary }} />,
      sections: [
        {
          title: t('pricing.info.transparency.usage.title') || fallbackData.transparency.usage.title,
          items: getItems('pricing.info.transparency.usage.items', fallbackData.transparency.usage.items)
        },
        {
          title: t('pricing.info.transparency.dashboard.title') || fallbackData.transparency.dashboard.title,
          items: getItems('pricing.info.transparency.dashboard.items', fallbackData.transparency.dashboard.items)
        }
      ]
    },
    {
      id: 'features',
      title: t('pricing.info.features.title') || fallbackData.features.title,
      icon: <FeaturedPlayListIcon sx={{ fontSize: 40, color: saasModernTheme.colors.primary }} />,
      sections: [
        {
          title: t('pricing.info.features.core.title') || fallbackData.features.core.title,
          items: getItems('pricing.info.features.core.items', fallbackData.features.core.items)
        },
        {
          title: t('pricing.info.features.enterprise.title') || fallbackData.features.enterprise.title,
          items: getItems('pricing.info.features.enterprise.items', fallbackData.features.enterprise.items)
        }
      ]
    }
  ];

  const getGradientDirection = (index) => {
    if (index === 0) return 'BOTTOM_LEFT';
    if (index === 1) return 'TOP_LEFT';
    return 'BOTTOM_RIGHT';
  };

  return (
    <Box sx={{ py: 8, px: 2 }}>
      {/* Section Title */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: ['32px', '48px'],
            fontFamily: saasModernTheme.fontFamily,
            fontWeight: 800,
            mb: 2,
            color: saasModernTheme.colors.black,
          }}
        >
          {t('pricing.info.main.title') || 'Transparent and Flexible Pricing'}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: ['16px', '18px'],
            color: saasModernTheme.colors.text,
            maxWidth: '600px',
            mx: 'auto',
          }}
        >
          {t('pricing.info.main.subtitle') || 'Understanding how Swiftask works'}
        </Typography>
      </Box>

      {/* Cards Grid */}
      <Grid container spacing={4} justifyContent="center">
        {cardData.map((card, index) => (
          <Grid item xs={12} md={4} key={card.id}>
            <Card
              sx={{
                ...gradientBoxStyleWithHoverAnimation(getGradientDirection(index)),
                height: '100%',
                p: 3,
                border: 'none',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 0 }}>
                {/* Card Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  {card.icon}
                  <Typography
                    variant="h5"
                    sx={{
                      ml: 2,
                      fontWeight: 700,
                      fontSize: ['18px', '20px'],
                      color: saasModernTheme.colors.black,
                    }}
                  >
                    {card.title}
                  </Typography>
                </Box>

                {/* Card Sections */}
                {card.sections.map((section, sectionIndex) => (
                  <Box key={sectionIndex} sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: '16px',
                        mb: 1.5,
                        color: saasModernTheme.colors.black,
                        background: saasModernTheme.customColor.gradiantColor,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {section.title}
                    </Typography>
                    
                    {section.items && Array.isArray(section.items) && section.items.map((item, itemIndex) => (
                      <Box
                        key={itemIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: 1,
                        }}
                      >
                        <CheckIcon
                          sx={{
                            color: saasModernTheme.colors.primary,
                            fontSize: 16,
                            mt: 0.2,
                            mr: 1,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '14px',
                            color: saasModernTheme.colors.text,
                            lineHeight: 1.5,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PricingInfoCards;
